import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import {
  directusLogin,
  directusLogout,
  directusMe,
  directusRefresh,
  directusRegister,
} from '../apis/auth';

const ACCESS_TOKEN_KEY = 'directus_access_token';
const REFRESH_TOKEN_KEY = 'directus_refresh_token';

const defaultState = {
  isLoading: true,
  user: null,
};

export const AuthContext = createContext({
  ...defaultState,
  login: async () => {},
  logout: async () => {},
  register: async () => {},
  isAuthenticated: false,
});

const isBrowser = typeof window !== 'undefined';

const readTokens = () => {
  if (!isBrowser) return { accessToken: null, refreshToken: null };

  return {
    accessToken: window.localStorage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: window.localStorage.getItem(REFRESH_TOKEN_KEY),
  };
};

const writeTokens = ({ accessToken, refreshToken }) => {
  if (!isBrowser) return;

  if (accessToken) {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  }

  if (refreshToken) {
    window.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

const clearTokens = () => {
  if (!isBrowser) return;

  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState(defaultState);

  const hydrateUser = useCallback(async () => {
    const { accessToken, refreshToken } = readTokens();

    if (!accessToken) {
      setState({ isLoading: false, user: null });
      return;
    }

    try {
      const user = await directusMe({ accessToken });
      setState({ isLoading: false, user });
    } catch (error) {
      if (!refreshToken) {
        clearTokens();
        setState({ isLoading: false, user: null });
        return;
      }

      try {
        const refreshed = await directusRefresh({ refreshToken });
        writeTokens(refreshed);
        const user = await directusMe({ accessToken: refreshed.accessToken });
        setState({ isLoading: false, user });
      } catch {
        clearTokens();
        setState({ isLoading: false, user: null });
      }
    }
  }, []);

  useEffect(() => {
    hydrateUser();
  }, [hydrateUser]);

  const login = useCallback(async ({ email, password }) => {
    const tokens = await directusLogin({ email, password });
    if (!tokens?.accessToken) {
      throw new Error('Login failed');
    }

    writeTokens(tokens);
    const user = await directusMe({ accessToken: tokens.accessToken });
    setState({ isLoading: false, user });

    return user;
  }, []);

  const logout = useCallback(async () => {
    const { refreshToken } = readTokens();

    try {
      await directusLogout({ refreshToken });
    } finally {
      clearTokens();
      setState({ isLoading: false, user: null });
    }
  }, []);

  const register = useCallback(async ({ email, password, firstName, lastName }) => {
    await directusRegister({ email, password, firstName, lastName });
  }, []);

  const value = useMemo(() => {
    return {
      ...state,
      login,
      logout,
      register,
      isAuthenticated: Boolean(state.user),
    };
  }, [state, login, logout, register]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
